"use client";

import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { ThemeProvider, useTheme } from "next-themes";

import type { IconProps } from "./icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { IconButton } from "./icon-button";

const THEMES = ["light", "dark", "system"] as const;

export type ThemeName = (typeof THEMES)[number];

const THEME_ICONS: Record<ThemeName, LucideIcon> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

export interface ThemeMessages {
  theme: Record<ThemeName, string> & {
    /** Accessible name of the trigger — the only label a screen reader gets. */
    label: string;
  };
}

const isThemeName = (value: string | undefined): value is ThemeName =>
  THEMES.includes(value as ThemeName);

function ThemeGlyph(props: IconProps) {
  return (
    <>
      <Sun
        {...props}
        className="scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90"
      />
      <Moon
        {...props}
        className="absolute scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0"
      />
    </>
  );
}

function ThemeToggle({ messages }: { messages: ThemeMessages }) {
  const { setTheme, theme } = useTheme();
  // next-themes only knows the stored choice after mount, so the first client
  // render has to match the server's — "system", which is also the default.
  // The Sun/Moon pair stays CSS-driven, so only Monitor waits for the mount.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  const active: ThemeName = mounted && isThemeName(theme) ? theme : "system";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          variant="ghost"
          icon={active === "system" ? Monitor : ThemeGlyph}
          label={messages.theme.label}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={active}
          onValueChange={(value: string) => {
            // The radio group hands back a bare string; only the three names
            // this toggle offers may reach next-themes.
            if (isThemeName(value)) setTheme(value);
          }}
        >
          {THEMES.map((name) => {
            const Icon = THEME_ICONS[name];
            return (
              <DropdownMenuRadioItem
                key={name}
                value={name}
                className="gap-2 cursor-pointer capitalize"
              >
                <Icon className="size-4" />
                {messages.theme[name]}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { THEMES, ThemeProvider, ThemeToggle };
